/* ===========================================================================
   Inline product-image zoom.

   Dawn only ships lightbox / hover / none. Hover is dead on touch and the
   lightbox throws a modal over the page — neither is what you want on a phone.
   This zooms the image in place: tap to magnify at the point you touched,
   drag to pan, pinch with two fingers, tap again to reset.

   Attaches inside one section only. Neutralises Dawn's .product__media-toggle
   overlay, which would otherwise swallow every tap and open the modal.
   =========================================================================== */
(() => {
  const MAX = 4;
  const TAP_SCALE = 2.6;
  const MOVE_TOLERANCE = 8; // px of travel still counted as a tap

  class InlineZoom {
    constructor(container) {
      this.box = container;
      this.img = container.querySelector('.product__media img');
      if (!this.img) return;

      this.scale = 1;
      this.tx = 0;
      this.ty = 0;
      this.pointers = new Map();
      this.pinchStart = null;
      this.didPinch = false;
      this.moved = 0;

      this.box.classList.add('dbp-zoom');
      this.img.classList.add('dbp-zoom__img');

      // Dawn's overlay button would open the lightbox on every tap, but it is also
      // the ONLY keyboard-operable way to magnify the photo. Keep it focusable and
      // in the accessibility tree; just repurpose it to drive this zoom instead.
      // Removing it outright was a WCAG 2.1.1 failure: pointer-only magnification.
      this.toggle = container.querySelector('.product__media-toggle');
      if (this.toggle) {
        this.toggle.classList.add('dbp-zoom__toggle-off');
        this.toggle.removeAttribute('aria-haspopup');
        this.toggle.setAttribute('aria-pressed', 'false');
        this.label = this.toggle.querySelector('.visually-hidden');
        this.setToggleLabel(false);
        this.toggle.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          if (this.scale > 1) this.reset();
          else this.zoomToCentre();
        });
        this.toggle.addEventListener('keydown', (e) => this.keyPan(e));
      }
      const icon = container.querySelector('.product__media-icon');
      if (icon) icon.classList.add('dbp-zoom__icon-off');

      this.hint = document.createElement('span');
      this.hint.className = 'dbp-zoom__hint';
      this.hint.setAttribute('aria-hidden', 'true');
      this.hint.innerHTML =
        '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true"><circle cx="11" cy="11" r="7"/><path d="M20 20l-3.6-3.6M11 8.5v5M8.5 11h5"/></svg><span>Tap to zoom</span>';
      this.box.appendChild(this.hint);

      this.bind();
    }

    bind() {
      const b = this.box;
      b.addEventListener('pointerdown', (e) => this.down(e));
      b.addEventListener('pointermove', (e) => this.move(e), { passive: false });
      b.addEventListener('pointerup', (e) => this.up(e));
      b.addEventListener('pointercancel', (e) => { this.cancelled = true; this.up(e); });
      b.addEventListener('dblclick', (e) => { e.preventDefault(); this.reset(); });
      document.addEventListener('keydown', (e) => { if (e.key === 'Escape') this.reset(); });
      // never let a wheel gesture scroll the page while zoomed
      b.addEventListener('wheel', (e) => {
        if (this.scale === 1) return;
        e.preventDefault();
        this.setScale(this.scale - Math.sign(e.deltaY) * 0.3, e);
      }, { passive: false });
    }

    rect() { return this.box.getBoundingClientRect(); }

    setToggleLabel(zoomed) {
      if (!this.toggle) return;
      this.toggle.setAttribute('aria-pressed', zoomed ? 'true' : 'false');
      if (this.label) {
        this.label.textContent = zoomed
          ? 'Zoom out. Use the arrow keys to move around the image.'
          : 'Zoom in on the product image';
      }
    }

    zoomToCentre() {
      const r = this.rect();
      this.zoomAt(r.left + r.width / 2, r.top + r.height / 2);
      this.toggle?.focus();
    }

    /** Arrow keys pan while magnified — the keyboard equivalent of dragging. */
    keyPan(e) {
      if (this.scale === 1) return;
      const step = e.shiftKey ? 80 : 32;
      const moves = { ArrowLeft: [step, 0], ArrowRight: [-step, 0], ArrowUp: [0, step], ArrowDown: [0, -step] };
      const m = moves[e.key];
      if (!m) return;
      e.preventDefault();
      this.tx += m[0];
      this.ty += m[1];
      this.clamp();
      this.render(false);
    }

    down(e) {
      this.cancelled = false;
      // Throws NotFoundError for a pointer id the element does not own; if that
      // escapes, the rest of this handler never runs and pinch never arms.
      try { this.box.setPointerCapture?.(e.pointerId); } catch (_) {}
      this.pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
      this.moved = 0;
      this.startX = e.clientX;
      this.startY = e.clientY;
      this.panFrom = { tx: this.tx, ty: this.ty };

      if (this.pointers.size === 2) {
        const [a, b] = [...this.pointers.values()];
        this.pinchStart = { dist: Math.hypot(a.x - b.x, a.y - b.y), scale: this.scale };
      }
    }

    move(e) {
      if (!this.pointers.has(e.pointerId)) return;
      this.pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });

      if (this.pointers.size === 2 && this.pinchStart) {
        e.preventDefault();
        const [a, b] = [...this.pointers.values()];
        const dist = Math.hypot(a.x - b.x, a.y - b.y);
        this.didPinch = true;
        this.moved = Infinity; // a pinch is never a tap
        this.applyScale(this.pinchStart.scale * (dist / this.pinchStart.dist));
        return;
      }

      const dx = e.clientX - this.startX;
      const dy = e.clientY - this.startY;
      this.moved = Math.max(this.moved, Math.hypot(dx, dy));

      if (this.scale > 1) {
        e.preventDefault(); // stop the media slider and the page from scrolling
        this.tx = this.panFrom.tx + dx;
        this.ty = this.panFrom.ty + dy;
        this.clamp();
        this.render(false);
      }
    }

    up(e) {
      this.pointers.delete(e.pointerId);
      if (this.pointers.size < 2) this.pinchStart = null;

      if (this.pointers.size === 0 && this.didPinch) {
        this.didPinch = false;
        return; // lifting out of a pinch must not be read as a tap
      }

      if (this.cancelled) { this.cancelled = false; return; }
      if (this.moved <= MOVE_TOLERANCE && this.pointers.size === 0) {
        if (this.scale > 1) {
          this.reset();
        } else {
          this.zoomAt(e.clientX, e.clientY);
        }
      }
    }

    zoomAt(clientX, clientY) {
      const r = this.rect();
      const cx = clientX - r.left - r.width / 2;
      const cy = clientY - r.top - r.height / 2;
      this.scale = TAP_SCALE;
      // move the touched point toward the centre of the frame
      this.tx = -cx * (this.scale - 1);
      this.ty = -cy * (this.scale - 1);
      this.clamp();
      this.render(true);
    }

    applyScale(next) {
      const prev = this.scale;
      this.scale = Math.min(MAX, Math.max(1, next));
      if (this.scale === 1) { this.tx = 0; this.ty = 0; }
      else { const f = this.scale / prev; this.tx *= f; this.ty *= f; }
      this.clamp();
      this.render(false);
    }

    setScale(next, e) {
      if (next <= 1) return this.reset();
      this.applyScale(next);
    }

    clamp() {
      const r = this.rect();
      const maxX = (r.width * (this.scale - 1)) / 2;
      const maxY = (r.height * (this.scale - 1)) / 2;
      this.tx = Math.min(maxX, Math.max(-maxX, this.tx));
      this.ty = Math.min(maxY, Math.max(-maxY, this.ty));
    }

    render(animate) {
      const zoomed = this.scale > 1;
      this.box.classList.toggle('is-zoomed', zoomed);
      this.setToggleLabel(zoomed);
      this.img.style.transition = animate ? 'transform .22s ease' : 'none';
      this.img.style.transform = `translate(${this.tx}px, ${this.ty}px) scale(${this.scale})`;
    }

    reset() {
      if (this.scale === 1 && this.tx === 0 && this.ty === 0) return;
      this.scale = 1; this.tx = 0; this.ty = 0;
      this.render(true);
    }
  }

  window.dbpInlineZoom = (sectionId) => {
    const root = document.getElementById('shopify-section-' + sectionId) || document;
    const start = () => {
      root.querySelectorAll('.product-media-container.media-type-image').forEach((c) => {
        if (c.dataset.dbpZoom) return;
        c.dataset.dbpZoom = '1';
        new InlineZoom(c);
      });
    };
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', start);
    } else {
      start();
    }
  };
})();
